import { useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useAuth } from "../../hooks/useAuth";
import { formatDate } from "../../lib/format";
import { clientService } from "../../services/clientService";
import { workoutPlanService } from "../../services/workoutPlanService";
import type { WorkoutPlan } from "../../types/coaching";

export function ClientWorkoutsPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<WorkoutPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadPlans() {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const client = await clientService.getCurrentClient(user.id);
        const assignedPlans = client
          ? await workoutPlanService.listAssignedWorkoutPlansForClient(client.id)
          : [];
        if (mounted) setPlans(assignedPlans);
      } catch (caughtError) {
        console.error("Unable to load assigned workout plans", caughtError);
        if (mounted) setError("Unable to load your workout plans.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadPlans();

    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <div>
      <AdminPageHeader
        eyebrow="Client portal"
        title="Your workout plan"
        description="Read-only workouts assigned by your coach."
      />
      {loading ? <FullPageLoader label="Loading workouts" /> : null}
      {error ? <EmptyState title="Unable to load workouts.">{error}</EmptyState> : null}
      {!loading && !error && !plans.length ? (
        <EmptyState title="Your coach has not assigned a workout plan yet." />
      ) : null}
      <div className="space-y-5">
        {plans.map((plan) => (
          <AdminPanel
            description={`Assigned ${formatDate(plan.assigned_at)}${plan.starts_on ? `, starts ${formatDate(plan.starts_on)}` : ""}`}
            key={plan.id}
            title={plan.title}
          >
            <p className="mb-5 text-sm leading-6 text-graphite/75">{plan.description || "No description."}</p>
            <div className="space-y-4">
              {(plan.workout_plan_days ?? []).map((day) => (
                <div className="rounded-md border border-line bg-white p-4" key={day.id}>
                  <h2 className="font-semibold text-ink">{day.title}</h2>
                  {day.notes ? <p className="mt-1 text-sm text-graphite/70">{day.notes}</p> : null}
                  <div className="mt-3 space-y-2">
                    {(day.workout_plan_exercises ?? []).map((exercise) => (
                      <div className="rounded-md bg-field p-3 text-sm" key={exercise.id}>
                        <p className="font-medium text-ink">{exercise.exercise_name}</p>
                        <p className="mt-1 text-graphite/70">
                          {[exercise.sets ? `${exercise.sets} sets` : null, exercise.reps ? `${exercise.reps} reps` : null, exercise.rest ? `${exercise.rest} rest` : null].filter(Boolean).join(" - ") || "Details not set"}
                        </p>
                        {exercise.notes ? <p className="mt-1 text-graphite/70">{exercise.notes}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </AdminPanel>
        ))}
      </div>
    </div>
  );
}
